import React, { useEffect, useState } from 'react';
import { Sparkles, AlertTriangle, Lock, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Trial countdown banner pinned at the top of the app.
 *
 * Rendered states (driven by the `user.trial.state` field from /auth/me):
 *   • trial_active           — neutral blue, soft "X days left"
 *   • trial_expiring_soon    — amber, urgent "X days left, talk to sales"
 *   • trial_expired          — red, blocking "Read-only mode"
 *   • not_trial / pending    — null (banner hidden)
 *
 * The banner can be dismissed for the current session (sessionStorage) ONLY
 * when the trial isn't expired. The expired state is non-dismissable since
 * the user needs to know writes are blocked.
 *
 * Also listens to the `trial-expired` window event fired by the global fetch
 * wrapper in index.js; on that event, we show a toast + force the AuthContext
 * to re-fetch /auth/me so the banner flips into the expired state immediately.
 */
const SOFT_DISMISS_KEY = 'trial_banner_dismissed_session';
const SALES_EMAIL = 'sales@inzara.com';

export default function TrialBanner() {
  const { user, checkAuth } = useAuth();
  const [softDismissed, setSoftDismissed] = useState(
    () => { try { return sessionStorage.getItem(SOFT_DISMISS_KEY) === '1'; } catch { return false; } }
  );

  useEffect(() => {
    const handler = () => {
      // 402 from a write — re-fetch user so we know we're in expired state,
      // and surface a clear toast so the user understands why their action failed.
      if (checkAuth) checkAuth();
      toast.error('Your free trial has ended', {
        description: 'Edits are paused until you upgrade. Your data is safe and still viewable.',
        duration: 6000,
      });
    };
    window.addEventListener('trial-expired', handler);
    return () => window.removeEventListener('trial-expired', handler);
  }, [checkAuth]);

  if (!user || !user.trial) return null;
  const { state, days_remaining } = user.trial;
  if (state === 'not_trial' || state === 'pending_approval') return null;
  if (softDismissed && state === 'trial_active') return null;

  const isExpired = state === 'trial_expired';
  const isUrgent = state === 'trial_expiring_soon';

  const styles = isExpired
    ? { bg: 'bg-rose-500/15', border: 'border-rose-500/30', text: 'text-rose-200', accent: 'text-rose-300', Icon: Lock }
    : isUrgent
      ? { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-100', accent: 'text-amber-300', Icon: AlertTriangle }
      : { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-100', accent: 'text-blue-300', Icon: Sparkles };

  const message = isExpired
    ? 'Your free trial has ended — read-only mode is active.'
    : (days_remaining === 0
        ? 'Your trial ends today.'
        : days_remaining === 1
          ? '1 day left in your trial.'
          : `${days_remaining} days left in your trial.`);

  const cta = isExpired ? 'Upgrade now' : 'Talk to sales';
  const handleSoftDismiss = () => {
    setSoftDismissed(true);
    try { sessionStorage.setItem(SOFT_DISMISS_KEY, '1'); } catch { /* no-op */ }
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2 border-b ${styles.bg} ${styles.border} ${styles.text}`}
      data-testid={`trial-banner-${state}`}
    >
      <styles.Icon className={`w-4 h-4 flex-shrink-0 ${styles.accent}`} />
      <div className="flex-1 text-xs font-medium leading-snug">
        <span data-testid="trial-banner-message">{message}</span>
        {isExpired && (
          <span className="ml-2 text-rose-300/80 hidden sm:inline">Your data is safe — switch to a paid plan to keep editing.</span>
        )}
      </div>
      <a
        href={`mailto:${SALES_EMAIL}?subject=INZARA%20-%20Upgrade%20my%20trial`}
        className={`text-xs font-semibold px-3 py-1 rounded-lg border ${styles.border} ${styles.accent} hover:bg-white/5 transition-colors`}
        data-testid="trial-banner-cta"
      >
        {cta}
      </a>
      {!isExpired && state === 'trial_active' && (
        <button
          onClick={handleSoftDismiss}
          className="text-zinc-400 hover:text-white p-0.5 rounded transition-colors"
          aria-label="Dismiss for this session"
          data-testid="trial-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
